import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { Badge } from '@/shared/components/Badge'
import type { ColorDeBadge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { Checkbox } from '@/shared/components/Checkbox'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, CreditCard, Loader2, FileText, Download, X, MoreVertical, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { usePurchaseOrderDetail, llaveDeOrden } from '@/hooks/usePurchaseOrderDetail'
import { LLAVE_ORDENES_RH } from '@/hooks/usePurchaseOrdersRH'
import { formatCurrency, formatDate, getSupplierName } from '@/features/proveedores/lib/purchaseOrderUtils'
import { purchaseOrdersRHService } from '@/features/rh/api/purchaseOrdersRH.service'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2 } from 'lucide-react'
import toast from 'react-hot-toast'
import type { PurchaseOrderRHLine, Invoice } from '@/types/purchaseOrderRH.types'
import { useAuth } from '@/shared/hooks/useAuth'
import { userHasPermission } from '@/shared/lib/roleUtils'
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage'
import { TruncatedText } from '@/shared/components/TruncatedText'
// ← NUEVO: imagen del modal de éxito
import successModalSvg from '@/assets/success-modal.svg'
import { AddPaymentModal, type PaymentFormData } from '@/features/rh/components/rh/AddPaymentModal'

const CalendarFolded = ({ className, ...props }: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M16 3H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12l5-5V5a2 2 0 0 0-2-2Z" />
    <path d="M21 14h-5a2 2 0 0 0-2 2v5" />
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <path d="M2 9h19" />
  </svg>
);

export default function PurchaseOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: order, isLoading, error } = usePurchaseOrderDetail(orderId!)
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = (searchParams.get('tab') as 'lines' | 'payments' | 'invoices' | null) ?? 'lines'
  const setActiveTab = (tab: 'lines' | 'payments' | 'invoices') =>
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('tab', tab); return next; }, { replace: true })

  // Invoice state
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false)
  const [showInvoiceLinesModal, setShowInvoiceLinesModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)

  const loadInvoices = useCallback(async () => {
    if (!orderId) return
    setIsLoadingInvoices(true)
    try {
      const data = await purchaseOrdersRHService.getInvoices(orderId)
      setInvoices(data)
    } catch (error) {
      console.error('Error loading invoices:', error)
      toast.error('Error al cargar las facturas')
    } finally {
      setIsLoadingInvoices(false)
    }
  }, [orderId])

  useEffect(() => {
    if (orderId && activeTab === 'invoices') {
      loadInvoices()
    }
  }, [orderId, activeTab, loadInvoices])

  // Selection state
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  // ← NUEVO: controla el modal de éxito
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showConfirmReleaseModal, setShowConfirmReleaseModal] = useState(false)
  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(new Set())

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode)
    setSelectedLineIds(new Set())
    setActiveTab('lines')
  }

  const toggleLineSelection = (lineId: string) => {
    const line = order?.lines?.find((l) => l.id === lineId)
    if (line?.erp_status === 'LIBERADA_PARA_PROVEEDOR') {
      toast('Esta línea ya fue liberada para facturación', { icon: 'ℹ️' })
      return
    }

    const newSelected = new Set(selectedLineIds)
    if (newSelected.has(lineId)) {
      newSelected.delete(lineId)
    } else {
      newSelected.add(lineId)
    }
    setSelectedLineIds(newSelected)
  }

  const toggleSelectAll = () => {
    if (!order) return
    const allLines = order.lines || []
    if (allLines.length === 0) return

    // Filter out lines that are already released
    const selectableLines = allLines.filter(l => l.erp_status !== 'LIBERADA_PARA_PROVEEDOR')
    if (selectableLines.length === 0) return

    const selectedSelectableCount = selectableLines.filter((l) =>
      selectedLineIds.has(l.id),
    ).length

    if (selectedSelectableCount === selectableLines.length) {
      setSelectedLineIds(new Set())
    } else {
      const allIds = new Set(selectableLines.map((l: PurchaseOrderRHLine) => l.id))
      setSelectedLineIds(allIds)
    }
  }

  const handleRowClick = (lineId: string) => {
    // Check if line is released
    const line = order?.lines?.find(l => l.id === lineId)
    if (line?.erp_status === 'LIBERADA_PARA_PROVEEDOR') {
      toast('Esta línea ya fue liberada para facturación', { icon: 'ℹ️' })
      return
    }

    if (!isSelectionMode) {
      setIsSelectionMode(true)
      setSelectedLineIds(new Set([lineId]))
    } else {
      toggleLineSelection(lineId)
    }
  }

  const queryClient = useQueryClient()

  const handleReleaseLines = async () => {
    if (selectedLineIds.size === 0) return

    const toastId = toast.loading('Habilitando líneas para facturación...')
    try {
      const response = await purchaseOrdersRHService.releaseLines(
        orderId!,
        Array.from(selectedLineIds)
      )

      if (response.errors && response.errors.length > 0) {
        const errorContent = (
          <div className="flex flex-col gap-2">
            <p className="font-medium">
              {response.affected > 0
                ? `Se habilitaron ${response.affected} de ${response.totalRequested} líneas para facturación.`
                : 'No se pudieron habilitar las líneas para facturación.'}
            </p>
            <ul className="list-disc pl-4 text-sm space-y-1">
              {response.errors.map((err: any) => (
                <li key={err.lineId}>
                  <span className="font-medium">{err.topic}:</span> {err.message}
                </li>
              ))}
            </ul>
          </div>
        )

        if (response.affected > 0) {
          toast.success(errorContent, { id: toastId, duration: 5000 })
        } else {
          toast.error(errorContent, { id: toastId, duration: 5000 })
        }
      } else {
        // ← MODIFICADO: muestra modal de éxito en lugar de toast
        toast.dismiss(toastId)
        setShowSuccessModal(true)
      }

      // Reset selection
      setSelectedLineIds(new Set())
      setIsSelectionMode(false)

      // Refresh data
      queryClient.invalidateQueries({ queryKey: llaveDeOrden(orderId!) })
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error releasing lines:', error)
      const errorMessage = error.response?.data?.message || 'Error al habilitar líneas para facturación'
      toast.error(errorMessage, { id: toastId })
    }
  }

  // Payment modal state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [paymentModalInvoiceId, setPaymentModalInvoiceId] = useState<string | undefined>(undefined)

  const openPaymentModal = (invoiceId: string) => {
    setPaymentModalInvoiceId(invoiceId)
    setIsPaymentModalOpen(true)
  }

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const [approveModalInvoiceId, setApproveModalInvoiceId] = useState<string | null>(null)
  const [creditDays, setCreditDays] = useState('')
  const [creditDaysError, setCreditDaysError] = useState<string | null>(null)
  const [showApproveSuccessModal, setShowApproveSuccessModal] = useState(false)
  const [rejectModalInvoiceId, setRejectModalInvoiceId] = useState<string | null>(null)
  const [_rejectReason, setRejectReason] = useState('')
  const [isRejecting, setIsRejecting] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdownId(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const closeApproveModal = () => {
    setApproveModalInvoiceId(null)
    setCreditDays('')
    setCreditDaysError(null)
  }

  const handleApproveInvoice = async (invoiceId: string) => {
    if (!orderId) return
    // Validación: entero 1-60, no vacío. Fuera de rango o vacío no se guarda.
    const parsed = Number(creditDays)
    if (creditDays.trim() === '' || !Number.isInteger(parsed) || parsed < 1 || parsed > 60) {
      setCreditDaysError('Ingresa un número entero entre 1 y 60')
      return
    }
    try {
      await purchaseOrdersRHService.approveInvoice(orderId, invoiceId, parsed)
      closeApproveModal()
      loadInvoices()
      setShowApproveSuccessModal(true)
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      toast.error(error.response?.data?.message || 'Error al aprobar la factura')
    }
  }

  const handleRejectInvoice = async () => {
    if (!orderId || !rejectModalInvoiceId) return
    setIsRejecting(true)
    try {
      await purchaseOrdersRHService.rejectInvoice(orderId, rejectModalInvoiceId, '')
      toast.success('Factura rechazada. El proveedor podrá corregirla.')
      setRejectModalInvoiceId(null)
      loadInvoices()
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      toast.error(error.response?.data?.message || 'Error al rechazar la factura')
    } finally {
      setIsRejecting(false)
    }
  }

  const handleAddPayment = async (data: PaymentFormData) => {
    if (!orderId) throw new Error('No se encontró la orden de compra')
    const ids = data.invoiceIds?.length ? data.invoiceIds : (data.invoiceId ? [data.invoiceId] : [])
    if (ids.length === 0) throw new Error('Agrega al menos una factura')
    await purchaseOrdersRHService.createBatchPayment({
      invoiceIds: ids,
      amount: data.amount,
      payment_date: data.payment_date,
      payment_method: data.payment_method,
      reference_number: data.reference_number,
      notes: data.notes,
      receipt: data.receipt,
    })
    toast.success('Pago registrado exitosamente')
    queryClient.invalidateQueries({ queryKey: ['po-payments', orderId] })
    queryClient.invalidateQueries({ queryKey: llaveDeOrden(orderId!) })
  }

  const { data: payments } = useQuery({
    queryKey: ['po-payments', orderId],
    queryFn: () => purchaseOrdersRHService.getAllPayments(orderId!),
    enabled: !!orderId
  });

  const totalPaidFromPayments = payments?.reduce((sum, p) => sum + Number(p.amount), 0) || 0;
  const totalPaid = order?.total_pagado != null
    ? Number(order.total_pagado || 0)
    : totalPaidFromPayments;
  const orderTotal = Number(order?.total || 0)
  const isPartiallyOrUnpaid = orderTotal > 0 && totalPaid < orderTotal
  const isFulfilled = String((order as any)?.fulfillment_status || '').toLowerCase() === 'surtido'

  const statusDisplay = (() => {
    if (order?.status === 'completed' && isPartiallyOrUnpaid) {
      if (isFulfilled) {
        return {
          label: 'Surtida',
          color: 'azul' as ColorDeBadge
        }
      }

      return {
        label: 'Pendiente de pago',
        color: 'amarillo' as ColorDeBadge
      }
    }

    const colorPorEstatus: Record<string, ColorDeBadge> = {
      draft: 'gris',
      pending: 'amarillo',
      confirmed: 'verde',
      rejected: 'rojo',
      in_progress: 'azul',
      completed: 'verde',
      cancelled: 'rojo',
    }

    return {
      label: purchaseOrdersRHService.getStatusLabel(order?.status || ''),
      color: colorPorEstatus[order?.status || ''] ?? ('gris' as ColorDeBadge)
    }
  })()

  const getInvoiceStatusMeta = (status?: string) => {
    switch (status) {
      case 'approved':
        return { label: 'Aprobada', color: 'verde' as ColorDeBadge }
      case 'rejected':
        return { label: 'Rechazada', color: 'rojo' as ColorDeBadge }
      case 'observed':
        return { label: 'Observada', color: 'naranja' as ColorDeBadge }
      case 'received':
        return { label: 'Recibida', color: 'azul' as ColorDeBadge }
      default:
        return { label: 'En revisión', color: 'azul' as ColorDeBadge }
    }
  }

  if (
    userHasPermission(
      'proveedores.administracion.ordenes_compra.consultar',
      user?.rbac?.effectivePermissions || []
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">Error al cargar la orden de compra</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/rh/purchase-orders')}
          className={estiloDeBoton('secundario', 'mb-4')}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Volver</span>
        </button>
        <div className="flex items-center justify-between">
          <div>
            <TituloDePantalla nivel="interna" className="mb-1">Ver orden de compra</TituloDePantalla>
            <p className="text-gray-600 dark:text-gray-400">Consulta los detalles para la orden de compra</p>
          </div>
        </div>
      </div>

      {/* Main Info Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <FileText className="w-6 h-6" style={{ color: "#2563eb" }} />
            <span className="text-lg font-medium text-gray-900 dark:text-white">
              {order.po_number}
            </span>
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 ml-2">
              <CalendarFolded className="w-4 h-4" />
              <span className="text-sm">{formatDate(order.created_at)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Badge color={statusDisplay.color}>{statusDisplay.label}</Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-8 mb-8">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Proveedor</p>
            <p className="text-base font-normal text-gray-900 dark:text-white">
              {order.supplier ? getSupplierName(order.supplier) : 'Sin proveedor'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Importe total</p>
            <p className="text-base font-normal text-gray-900 dark:text-white">
              {formatCurrency(order.total, order.currency, false)}
            </p>
          </div>
          {/* <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Centro de costos:</p>
            <p className="text-base font-normal text-gray-900 dark:text-white">
              {order.cost_center || 'N/A'}
            </p>
          </div> */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Fecha requerida</p>
            <p className="text-base font-normal text-gray-900 dark:text-white">
              {formatDate(order.delivery_date)}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Moneda</p>
            <p className="text-base font-normal text-gray-900 dark:text-white">
              {order.currency}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Total pagado</p>
            <p className="text-base font-normal text-gray-900 dark:text-white">
              ${Number(totalPaid).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {/* / ${Number(order.total).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} */}
            </p>
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Descripción</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {order.notes || 'Sin descripción'}
          </p>
        </div>
      </div>


      {/* Actions Toolbar */}
      {
        activeTab === 'lines' && (
          <div className="flex justify-end gap-4 mb-4">
            {isSelectionMode && (
              <div className="flex items-center mr-auto">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedLineIds.size} seleccionados
                </span>
              </div>
            )}

            {!isSelectionMode ? (
              <button
                onClick={() => {
                  const firstSelectableLine = order.lines?.find(
                    (line) => line.erp_status !== 'LIBERADA_PARA_PROVEEDOR',
                  )

                  if (!firstSelectableLine) {
                    toast('No hay líneas disponibles para facturar', { icon: 'ℹ️' })
                    return
                  }

                  handleRowClick(firstSelectableLine.id)
                }}
                title="Habilita las líneas seleccionables para que el proveedor pueda facturarlas"
                className={estiloDeBoton('primario')}
              >
                <CheckCircle2 className="w-4 h-4" />
                Habilitar para facturación
              </button>
            ) : (
              <>
                <button
                  onClick={toggleSelectionMode}
                  className={estiloDeBoton('secundario')}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => setShowConfirmReleaseModal(true)}
                  disabled={selectedLineIds.size === 0}
                  title="Habilita las líneas seleccionadas para que el proveedor pueda facturarlas"
                  className={estiloDeBoton('primario')}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Habilitar para facturación
                </button>
              </>
            )}
          </div>
        )
      }

      {/* Tabs */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveTab('lines')}
          className={`px-6 py-2.5 font-medium text-sm transition-all rounded-lg border ${activeTab === 'lines'
            ? 'bg-[#2C3E7C] text-white border-[#2C3E7C]'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
        >
          Líneas de productos
        </button>
        <button
          onClick={() => setActiveTab('invoices')}
          className={`px-6 py-2.5 font-medium text-sm transition-all rounded-lg border ${activeTab === 'invoices'
            ? 'bg-[#2C3E7C] text-white border-[#2C3E7C]'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
        >
          Facturas cargadas
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={`px-6 py-2.5 font-medium text-sm transition-all rounded-lg border ${activeTab === 'payments'
            ? 'bg-[#2C3E7C] text-white border-[#2C3E7C]'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
        >
          Historial de pagos
        </button>
      </div>

      {/* Tab Content */}
      {
        activeTab === 'lines' && (
          <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">


            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[#E5E7EB]">
                <thead className="bg-[#FAF9FF]">
                  <tr>
                    {isSelectionMode && (
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        {(() => {
                          const selectableLines = (order.lines || []).filter(
                            (line) => line.erp_status !== 'LIBERADA_PARA_PROVEEDOR',
                          )
                          const selectedSelectableCount = selectableLines.filter((line) =>
                            selectedLineIds.has(line.id),
                          ).length

                          return (
                            <Checkbox
                              checked={
                                selectableLines.length > 0 &&
                                selectedSelectableCount === selectableLines.length
                              }
                              onChange={toggleSelectAll}
                            />
                          )
                        })()}
                      </th>
                    )}
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">#</th>
                    <th className="px-6 py-4 min-w-[140px] text-left text-sm font-medium text-[#1B2128] tracking-wider">Código</th>
                    <th className="px-6 py-4 min-w-[220px] text-left text-sm font-medium text-[#1B2128] tracking-wider">Descripción</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">Cantidad</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">Surtidos</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Unidad</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estado</th>
                    <th className="px-6 py-4 min-w-[145px] text-right text-sm font-medium text-[#1B2128] tracking-wider">Precio unitario</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-[#1B2128] tracking-wider">Descuento%</th>
                    <th className="px-6 py-4 min-w-[145px] text-right text-sm font-medium text-[#1B2128] tracking-wider">Subtotal</th>
                    <th className="px-6 py-4 min-w-[145px] text-right text-sm font-medium text-[#1B2128] tracking-wider">IVA</th>
                    <th className="px-6 py-4 min-w-[145px] text-right text-sm font-medium text-[#1B2128] tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E1E1E1]">
                  {order.lines?.map((line) => (
                    (() => {
                      const isReleased = line.erp_status === 'LIBERADA_PARA_PROVEEDOR'
                      return (
                        <tr
                          key={line.id}
                          onClick={() => handleRowClick(line.id)}
                          className={`border-b !border-[#e5e7eb] transition-colors last:border-b-0 cursor-pointer ${isReleased ? 'bg-green-50/70 hover:bg-green-50' : 'hover:bg-[#F4F7FB]'}`}
                        >
                          {isSelectionMode && (
                            <td className="px-4 py-3 text-xs" onClick={(e) => e.stopPropagation()}>
                              <Checkbox conservarOpacidad
                                checked={isReleased ? true : selectedLineIds.has(line.id)}
                                onChange={() => toggleLineSelection(line.id)}
                                disabled={isReleased}
                              />
                            </td>
                          )}
                          <td className="px-4 py-3 text-xs text-gray-900 dark:text-white">{line.line_number}</td>
                          <td className="px-4 py-3 text-xs text-gray-900 dark:text-white">
                            <TruncatedText text={line.product_code} maxWidthClass="max-w-[130px]" className="text-gray-900 dark:text-white" />
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-900 dark:text-white">
                            <TruncatedText text={line.product_name} maxWidthClass="max-w-[220px]" className="text-gray-900 dark:text-white" />
                          </td>
                          <td className="px-4 py-3 text-xs text-center text-gray-900 dark:text-white">{line.quantity}</td>
                          <td className="px-4 py-3 text-xs text-center text-gray-900 dark:text-white">{line.quantity_received ?? 0}</td>
                          <td className="px-4 py-3 text-xs text-gray-900 dark:text-white">{line.unit_of_measure}</td>
                          <td className="px-4 py-3 text-xs">
                            {isReleased ? (
                              <Badge color="verde" icon={<CheckCircle2 className="w-3 h-3" />}>
                                Liberada
                              </Badge>
                            ) : (
                              <Badge color="gris">No liberada</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-right text-gray-900 dark:text-white">{formatCurrency(line.unit_price, order.currency)}</td>
                          <td className="px-4 py-3 text-xs text-right text-gray-900 dark:text-white">{line.discount_percent}%</td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-right text-gray-900 dark:text-white">{formatCurrency(line.subtotal, order.currency)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-right text-gray-900 dark:text-white">{formatCurrency(line.tax_amount, order.currency)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-right font-medium text-gray-900 dark:text-white">{formatCurrency(line.total, order.currency)}</td>
                        </tr>
                      )
                    })()
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      }

      {
        activeTab === 'payments' && (
          <PaymentsTab orderId={orderId!} navigate={navigate} />
        )
      }

      {
        activeTab === 'invoices' && (
          <div className="space-y-6">
            {/* Invoices List */}
            {isLoadingInvoices ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 ml-3">Cargando facturas...</p>
                </div>
              </div>
            ) : invoices.length > 0 ? (
              <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#E5E7EB]">
                    <thead className="bg-[#FAF9FF]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Factura</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estatus</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">XML</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">PDF</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Notas</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Archivos</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Partidas relacionadas</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#E1E1E1]">
                      {invoices.map((invoice, index) => {
                        const invoiceStatus = getInvoiceStatusMeta(invoice.status)
                        return (
                          <tr
                            key={invoice.id}
                            className={`hover:bg-[#F4F7FB] transition-colors ${!invoice.xml_url || !invoice.pdf_url ? 'bg-[#f2f8ff]' : ''
                              }`}
                          >
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {invoice.supplier_invoice_number || `Factura #${index + 1}`}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                UUID: {invoice.invoice_uuid || 'No disponible'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Folio fiscal: {invoice.fiscal_folio || 'No disponible'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Emisión: {invoice.invoice_issued_at ? formatDate(invoice.invoice_issued_at) : 'No disponible'}
                              </p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-left">
                              <Badge color={invoiceStatus.color}>{invoiceStatus.label}</Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {invoice.xml_url ? (
                                <a
                                  href={invoice.xml_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
                                  title="Descargar XML"
                                >
                                  <Download className="w-4 h-4" />
                                  <span className="text-xs font-medium underline">Descargar</span>
                                </a>
                              ) : (
                                <span className="text-xs text-gray-400 font-medium">Falta XML</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {invoice.pdf_url ? (
                                <a
                                  href={invoice.pdf_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
                                  title="Ver PDF"
                                >
                                  <FileText className="w-4 h-4" />
                                  <span className="text-xs font-medium underline">Descargar</span>
                                </a>
                              ) : (
                                <span className="text-xs text-gray-400 font-medium">Falta PDF</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs break-words">
                              {invoice.validation_notes ? (
                                <span>{invoice.validation_notes}</span>
                              ) : (
                                <span className="text-gray-400">N/A</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {!invoice.xml_url || !invoice.pdf_url ? (
                                <Badge color="rojo">Archivo incompleto</Badge>
                              ) : (
                                <Badge color="verde">Completo</Badge>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium cursor-pointer"
                                onClick={() => {
                                  setSelectedInvoice(invoice)
                                  setShowInvoiceLinesModal(true)
                                }}
                              >
                                Ver partidas
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              {(() => {
                                const invoicePaid = invoice.payments?.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) ?? 0
                                const invoiceTotal = invoice.invoice_total != null ? Number(invoice.invoice_total) : null
                                const invoiceRemaining = invoiceTotal != null ? Math.max(0, invoiceTotal - invoicePaid) : null
                                const isFullyPaid = invoiceRemaining !== null && invoiceRemaining <= 0.01
                                if (invoice.status === 'approved' && !isFullyPaid) {
                                  return (
                                    <button
                                      onClick={() => openPaymentModal(invoice.id)}
                                      className={estiloDeBoton('primario')}
                                    >
                                      <CheckCircle2 className="w-4 h-4" />
                                      Pagar
                                    </button>
                                  )
                                }
                                if (invoice.status !== 'approved') {
                                  return (
                                    <div className="relative" ref={openDropdownId === invoice.id ? dropdownRef : undefined}>
                                      <button
                                        onClick={() => setOpenDropdownId(openDropdownId === invoice.id ? null : invoice.id)}
                                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
                                      >
                                        <MoreVertical className="w-5 h-5" />
                                      </button>
                                      {openDropdownId === invoice.id && (
                                        <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                                          <button
                                            onClick={() => { setApproveModalInvoiceId(invoice.id); setCreditDays(''); setCreditDaysError(null); setOpenDropdownId(null) }}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                                          >
                                            <CheckCircle className="w-4 h-4 text-green-500" />
                                            Aprobar
                                          </button>
                                          <button
                                            onClick={() => { setRejectModalInvoiceId(invoice.id); setOpenDropdownId(null) }}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg"
                                          >
                                            <XCircle className="w-4 h-4 text-red-500" />
                                            Rechazar
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )
                                }
                                return null
                              })()}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <p className="text-gray-500 dark:text-gray-400">No hay facturas cargadas</p>
              </div>
            )}
          </div>
        )
      }

      {/* Invoice Lines Modal */}
      {
        showInvoiceLinesModal && selectedInvoice && (
          <CapaDeModal>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-[95vw] overflow-hidden flex flex-col max-h-[90vh]">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-white dark:bg-gray-800 sticky top-0 z-10">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Factura #{invoices.indexOf(selectedInvoice) + 1}</h3>
                  <Badge color={getInvoiceStatusMeta(selectedInvoice.status).color}>
                    {getInvoiceStatusMeta(selectedInvoice.status).label}
                  </Badge>
                </div>
                <button
                  onClick={() => setShowInvoiceLinesModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-4 overflow-y-auto">
                <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
                  <table className="min-w-full table-fixed divide-y divide-[#E5E7EB]">
                    <thead className="bg-[#FAF9FF]">
                      <tr>
                        <th className="w-10 px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">#</th>
                        <th className="w-24 px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Código</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Descripción</th>
                        <th className="w-20 px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Cantidad</th>
                        <th className="w-20 px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Unidad</th>
                        <th className="w-28 px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Precio unit.</th>
                        <th className="w-16 px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Desc.%</th>
                        <th className="w-28 px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Subtotal</th>
                        <th className="w-28 px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#E1E1E1]">
                      {selectedInvoice.lines?.map((line) => (
                        <tr key={line.id} className="hover:bg-[#F4F7FB] transition-colors">
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{line.line_number}</td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 truncate">{line.product_code}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{line.product_name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{line.quantity}</td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{line.unit_of_measure}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">${Number(line.unit_price).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{line.discount_percent}%</td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">${(Number(line.quantity) * Number(line.unit_price)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">${Number(line.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      )) || (
                          <tr>
                            <td colSpan={9} className="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
                              No hay partidas asociadas a esta factura.
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </CapaDeModal>
        )
      }

      {/* Modal éxito aprobar factura */}
      {showApproveSuccessModal && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-8">
            <div className="flex justify-end mb-2">
              <button onClick={() => setShowApproveSuccessModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-6">
              ¡Su factura ha sido aprobada correctamente!
            </h3>
            <div className="flex justify-center mb-8">
              <img src={successModalSvg} alt="Éxito" className="w-24 h-24" />
            </div>
            <button
              onClick={() => setShowApproveSuccessModal(false)}
              className={estiloDeBoton('primario', 'w-full')}
            >
              Continuar
            </button>
          </div>
        </CapaDeModal>
      )}

      {/* Modal aprobar factura */}
      {approveModalInvoiceId && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-8">
            <div className="flex justify-end mb-2">
              <button onClick={closeApproveModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-6">
              ¿Estas seguro de que deseas aprobar<br />la factura?
            </h3>

            {/* Días de crédito — define la fecha de vencimiento de la factura */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Días de crédito <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={1}
                max={60}
                step={1}
                value={creditDays}
                onChange={(e) => {
                  setCreditDays(e.target.value)
                  if (creditDaysError) setCreditDaysError(null)
                }}
                placeholder="Ej: 30"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C3E7C] focus:border-transparent ${
                  creditDaysError ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {creditDaysError ? (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {creditDaysError}
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-400">Ingresa un número del 1 al 60.</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeApproveModal}
                className={estiloDeBoton('secundario', 'flex-1')}
              >
                Cancelar
              </button>
              <button
                onClick={() => handleApproveInvoice(approveModalInvoiceId)}
                className={estiloDeBoton('exito', 'flex-1')}
              >
                Confirmar
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Modal rechazar factura */}
      {rejectModalInvoiceId && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-8">
            <div className="flex justify-end mb-2">
              <button onClick={() => { setRejectModalInvoiceId(null); setRejectReason('') }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-8">
              ¿Estas seguro de que deseas rechazar<br />la factura?
            </h3>
            <div className="flex gap-3">
              <button
                onClick={() => { setRejectModalInvoiceId(null); setRejectReason('') }}
                className={estiloDeBoton('secundario', 'flex-1')}
              >
                Cancelar
              </button>
              <button
                onClick={handleRejectInvoice}
                disabled={isRejecting}
                className={estiloDeBoton('peligro', 'flex-1')}
              >
                {isRejecting ? 'Rechazando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Modal de pago */}
      <AddPaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => { setIsPaymentModalOpen(false); setPaymentModalInvoiceId(undefined) }}
        onSubmit={handleAddPayment}
        invoices={invoices.filter(i => i.status === 'approved')}
        preselectedInvoiceId={paymentModalInvoiceId}
      />

      {/* Modal confirmación liberar líneas */}
      {showConfirmReleaseModal && (
        <CapaDeModal>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-8">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                ¿Estas seguro de que deseas liberar las líneas?
              </h2>
              <button
                onClick={() => setShowConfirmReleaseModal(false)}
                className="text-gray-400 hover:text-gray-600 ml-4 flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
              Se liberarán las líneas seleccionadas al confirmar
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmReleaseModal(false)}
                className={estiloDeBoton('secundario', 'flex-1')}
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  setShowConfirmReleaseModal(false)
                  await handleReleaseLines()
                }}
                className={estiloDeBoton('primario', 'flex-1')}
              >
                Confirmar
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Modal de éxito al liberar líneas */}
      {showSuccessModal && (
        <CapaDeModal>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm mx-4 p-8 flex flex-col items-center text-center relative">
            <button
              onClick={() => { setShowSuccessModal(false); queryClient.invalidateQueries({ queryKey: LLAVE_ORDENES_RH }) }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              ¡Sus líneas se han liberado correctamente!
            </h2>
            <img
              src={successModalSvg}
              alt="Éxito"
              className="w-24 h-24 mb-8"
            />
            <button
              onClick={() => { setShowSuccessModal(false); queryClient.invalidateQueries({ queryKey: LLAVE_ORDENES_RH }) }}
              className={estiloDeBoton('primario', 'w-full')}
            >
              Continuar
            </button>
          </div>
        </CapaDeModal>
      )}
    </div >
  )
}

// ─── PaymentsTab ────────────────────────────────────────────────────────────
function PaymentsTab({ orderId, navigate: _navigate }: { orderId: string; navigate: (path: string) => void }) {
  const [payments, setPayments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    purchaseOrdersRHService.getAllPayments(orderId)
      .then(setPayments)
      .catch(() => toast.error('Error al cargar pagos'))
      .finally(() => setLoading(false))
  }, [orderId])

  const getStatusMeta = (status: string) => {
    switch (status) {
      case 'approved': return { label: 'Pagado', color: 'verde' as ColorDeBadge }
      case 'pending': return { label: 'Pendiente', color: 'amarillo' as ColorDeBadge }
      default: return { label: status || 'N/A', color: 'gris' as ColorDeBadge }
    }
  }

  return (
    <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
          Historial de pagos
        </h3>

      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[#E5E7EB]">
          <thead className="bg-[#FAF9FF]">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Factura</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Monto</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Fecha de pago</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Método</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Referencia</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estado</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Complemento</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#E1E1E1]">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-10 text-center">
                  <div className="flex justify-center items-center gap-2 text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Cargando pagos...</span>
                  </div>
                </td>
              </tr>
            ) : payments.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-10 text-center">
                  <CreditCard className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">No hay pagos registrados</p>
                </td>
              </tr>
            ) : (
              payments.map((payment) => {
                const statusMeta = getStatusMeta(payment.invoice_status)
                return (
                  <tr key={payment.id} className="hover:bg-[#F4F7FB] transition-colors">
                    <td className="px-6 py-4 text-xs text-gray-900 dark:text-white">
                      {payment.invoice_number || '—'}
                    </td>
                    <td className="px-6 py-4 text-xs font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                      ${Number(payment.amount || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-900 dark:text-white whitespace-nowrap">
                      {purchaseOrdersRHService.formatDate(payment.payment_date)}
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-900 dark:text-white">
                      {payment.payment_method || '—'}
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-900 dark:text-white font-mono">
                      {payment.reference_number || '—'}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      <Badge color={statusMeta.color}>{statusMeta.label}</Badge>
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {(payment.complement_xml_url || payment.complement_pdf_url) ? (
                        <div className="flex items-center gap-2">
                          {payment.complement_xml_url && (
                            <a href={payment.complement_xml_url} download target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors">
                              <Download className="w-3 h-3" />XML
                            </a>
                          )}
                          {payment.complement_pdf_url && (
                            <a href={payment.complement_pdf_url} download target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded transition-colors">
                              <Download className="w-3 h-3" />PDF
                            </a>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">Sin complemento</span>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
